import tocDataRaw from "../../data/toc.json";
import manuscriptListRaw from "../../data/manuscripts.json";

import type { Work, Variant } from "@/types/document";

type Author = {
    name: string;
    edited?: boolean;
    excerpt?: boolean;
    title?: string;
};

type Manuscript = {
    id: string;
    [key: string]: unknown;
};

type ManuscriptGroup = {
    primaryLeadingManuscriptId: string;
    works: Work[];
    manuscript?: Manuscript;
    isOpenForDisplay?: boolean;
};

type GroupsAccumulator = {
    previousGroup: Set<string>;
    currentGroup: Set<string>;
    groups: ManuscriptGroup[];
};

const tocData = tocDataRaw as Work[];
const manuscriptList = manuscriptListRaw as Manuscript[];

/*
 * The following function groups the items in the table of contents by their primary leading manuscript.
 * This function assumes that the edition's work ids are ordered in sequence of their primary leading manuscripts.
 * For example: The works with ids 1, 2, 3 belong to the same primary leading manuscript (Mue53), the works with ids
 * 4-12 belong to the next primary leading manuscript (W), and so on. So this function only needs to find the boundaries
 * between the primary leading manuscripts and then group accordingly.
 */
function getPrimaryLeadingManuscriptGroups(): ManuscriptGroup[] {
    const grouped = tocData.reduce<GroupsAccumulator>(
        (acc, item) => {
            const primaryLeadingManuscriptSiglum = item.variants.find(
                (v) =>
                    v.leadingManuscriptOrder &&
                    v.leadingManuscriptOrder === "1",
            )?.id;

            if (acc.currentGroup.size === 0) {
                if (primaryLeadingManuscriptSiglum) {
                    acc.currentGroup.add(primaryLeadingManuscriptSiglum);
                } else {
                    item.variants.forEach((v) => acc.currentGroup.add(v.id));
                }

                acc.groups.push({
                    primaryLeadingManuscriptId: Array.from(
                        acc.currentGroup.values(),
                    ).join("-"),
                    works: [item],
                });
            } else {
                const lastGroup = acc.groups[acc.groups.length - 1];
                const lastWork = lastGroup.works[lastGroup.works.length - 1];

                if (parseInt(item.workId) === parseInt(lastWork.workId)) {
                    // the current item is a variant of the current group's work, even if it doesn't have the primary leading manuscript siglum
                    lastGroup.works.push(item);
                } else if (
                    primaryLeadingManuscriptSiglum &&
                    acc.currentGroup.has(primaryLeadingManuscriptSiglum)
                ) {
                    // make sure the current item's primary leading manuscript siglum is the only one in the current group
                    acc.currentGroup.clear();
                    acc.currentGroup.add(primaryLeadingManuscriptSiglum);

                    lastGroup.primaryLeadingManuscriptId =
                        primaryLeadingManuscriptSiglum;
                    lastGroup.works.push(item);
                } else if (primaryLeadingManuscriptSiglum) {
                    // if the current item has a primary leading manuscript siglum, but it's not in the current group, start a new group
                    acc.previousGroup = acc.currentGroup;
                    acc.currentGroup = new Set<string>();
                    acc.currentGroup.add(primaryLeadingManuscriptSiglum);

                    acc.groups.push({
                        primaryLeadingManuscriptId:
                            primaryLeadingManuscriptSiglum,
                        works: [item],
                    });
                } else {
                    // if the current item doesn't have a primary leading manuscript siglum, calculate the intersection of the current sigla and the previous sigla
                    const variantIds = item.variants.map((v) => v.id);
                    const intersectionWithCurrentGroup = new Set<string>(
                        [...acc.currentGroup].filter((sig) =>
                            variantIds.includes(sig),
                        ),
                    );

                    if (intersectionWithCurrentGroup.size > 0) {
                        // if the intersection is not empty, add the current item to the current group
                        acc.currentGroup = intersectionWithCurrentGroup;
                        lastGroup.primaryLeadingManuscriptId = Array.from(
                            intersectionWithCurrentGroup.values(),
                        ).join("-");
                        lastGroup.works.push(item);
                    } else if (item.variants.every((v) => v.id === "Sigle")) {
                        // "Sigle" is a default id stemming from oXygen code templates. It is an indicator that the
                        // source data has not been properly worked on yet. To prevent breaking the system, just add
                        // the current item to the current group.
                        lastGroup.works.push(item);
                    } else {
                        // start a new group
                        acc.previousGroup = acc.currentGroup;
                        acc.currentGroup = new Set<string>();
                        item.variants.forEach((v) =>
                            acc.currentGroup.add(v.id),
                        );

                        acc.groups.push({
                            primaryLeadingManuscriptId: Array.from(
                                acc.currentGroup.values(),
                            ).join("-"),
                            works: [item],
                        });
                    }
                }
            }

            return acc;
        },
        {
            previousGroup: new Set<string>(),
            currentGroup: new Set<string>(),
            groups: [],
        },
    );

    // add the manuscript data to each group
    grouped.groups.forEach((group) => {
        const siglum = group.primaryLeadingManuscriptId.split("-")[0];
        const manuscript = manuscriptList.find((m) => m.id === siglum);
        group.manuscript = manuscript;
        group.isOpenForDisplay = true;
    });

    return grouped.groups;
}

function getWorkById(id: string): Work | undefined {
    return tocData.find((w) => w.workId === id);
}

function getWorkList(): {
    workId: string;
    workTitle: string;
    authors: Author[];
    variants: Variant[];
}[] {
    return tocData
        .map((item) => {
            return {
                workId: item.workId,
                workTitle: item.workTitle,
                authors:
                    item.variants[0]?.authors.reduce<Author[]>((acc, a) => {
                        const name = a.name.trim();
                        if (
                            !acc.find(
                                (ac) =>
                                    ac.name === name &&
                                    ac.edited === a.edited &&
                                    ac.excerpt === a.excerpt &&
                                    ac.title === a.title,
                            )
                        ) {
                            acc.push(a);
                        }
                        return acc;
                    }, []) ?? [],
                variants: item.variants,
            };
        })
        .sort((a, b) => {
            if (a.workTitle < b.workTitle) return -1;
            if (a.workTitle > b.workTitle) return 1;
            return 0;
        });
}

export { getPrimaryLeadingManuscriptGroups, getWorkById, getWorkList, tocData };
